import { useEffect, useState } from "react";

export type LandingVariant = "A" | "B" | "C";

const CAMPAIGN_MAP: Record<string, LandingVariant> = {
  technical: "A",
  business: "B",
  response: "C",
};
const STORAGE_KEY = "landingVariant";

const trackVariantAssignment = (variant: LandingVariant) => {
  if (typeof window === "undefined") return;
  const _paq = (window as any)._paq as unknown[][] | undefined;
  _paq?.push(["trackEvent", "Landing Page", "landing_variant_assigned", variant]);
};

const setVariantInStorage = (variant: LandingVariant) => {
  const expiry = new Date().getTime() + 60 * 24 * 60 * 60 * 1000; // 60 days
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ variant, expiry }));
};

export const useLandingVariant = (): LandingVariant => {
  const [variant, setVariant] = useState<LandingVariant>(() => {
    // Initialize from localStorage on first render
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { variant: storedVariant, expiry } = JSON.parse(stored);
        if (new Date().getTime() < expiry) {
          return storedVariant;
        }
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
    return "C"; // Default fallback
  });

  useEffect(() => {
    // Check URL parameter on mount
    const params = new URLSearchParams(window.location.search);
    const campaignParam = params.get("lp");

    if (campaignParam && CAMPAIGN_MAP[campaignParam]) {
      const newVariant = CAMPAIGN_MAP[campaignParam];
      console.log(`[ShadowScan] Variant set to ${newVariant} via URL parameter lp=${campaignParam}`);
      setVariant(newVariant);
      setVariantInStorage(newVariant);
      trackVariantAssignment(newVariant);

      // Clean URL
      params.delete("lp");
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      window.history.replaceState({}, "", newUrl);
      return;
    }

    // If no stored variant and no URL param, assign default
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      console.log(`[ShadowScan] Variant set to C (default, first visit)`);
      setVariantInStorage("C");
      trackVariantAssignment("C");
    } else {
      console.log(`[ShadowScan] Variant restored from localStorage`);
    }
  }, []); // Run only once on mount

  return variant;
};
